import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { cameras } from '../cameraImageData';

const storeLocations = [
  {
    id: 1,
    name: 'Fountain (Inside)',
    location: '',
    customerFootfall: 151,
    employeeAvailability: 16,
    ratio: 9.44,
    evidenceCount: 112,
    status: 'active'
  },
  {
    id: 2,
    name: 'Gate 1 (Inside)',
    location: '',
    customerFootfall: 89,
    employeeAvailability: 12,
    ratio: 7.42,
    evidenceCount: 87,
    status: 'active'
  },
  {
    id: 3,
    name: 'Gate 2 (Inside)',
    location: '',
    customerFootfall: 203,
    employeeAvailability: 18,
    ratio: 11.28,
    evidenceCount: 156,
    status: 'active'
  },
  {
    id: 4,
    name: 'Gate 3 (Inside)',
    location: '',
    customerFootfall: 127,
    employeeAvailability: 14,
    ratio: 9.07,
    evidenceCount: 98,
    status: 'active'
  },
  {
    id: 5,
    name: 'Gate 4 (Inside)',
    location: '',
    customerFootfall: 165,
    employeeAvailability: 15,
    ratio: 11.0,
    evidenceCount: 134,
    status: 'active'
  }
];

// Demographics data for different dates with camera-wise breakdown
const demographicData = {
  15: {
    'Camera-001': {
      'Old Female': 570,
      'Old Male': 3428,
      'Adult Female': 2430,
      'Adult Male': 8376,
      'Kid Female': 174,
      'Kid Male': 1354
    },
    'Camera-002': {
      'Old Female': 70,
      'Old Male': 734,
      'Adult Female': 193,
      'Adult Male': 1131,
      'Kid Female': 12,
      'Kid Male': 193
    },
    'Camera-003': {
      'Old Female': 11,
      'Old Male': 267,
      'Adult Female': 236,
      'Adult Male': 1086,
      'Kid Female': 0,
      'Kid Male': 23
    },
    'Camera-004': {
      'Old Female': 21,
      'Old Male': 104,
      'Adult Female': 98,
      'Adult Male': 443,
      'Kid Female': 12,
      'Kid Male': 51
    },
    'Camera-005': {
      'Old Female': 7,
      'Old Male': 518,
      'Adult Female': 12,
      'Adult Male': 223,
      'Kid Female': 14,
      'Kid Male': 95
    }
  },
  16: {
    'Camera-001': {
      'Old Female': 520,
      'Old Male': 3127,
      'Adult Female': 2217,
      'Adult Male': 7642,
      'Kid Female': 159,
      'Kid Male': 1235
    },
    'Camera-002': {
      'Old Female': 114,
      'Old Male': 1188,
      'Adult Female': 313,
      'Adult Male': 1832,
      'Kid Female': 19,
      'Kid Male': 312
    },
    'Camera-003': {
      'Old Female': 23,
      'Old Male': 520,
      'Adult Female': 460,
      'Adult Male': 2117,
      'Kid Female': 0,
      'Kid Male': 45
    },
    'Camera-004': {
      'Old Female': 241,
      'Old Male': 1184,
      'Adult Female': 1124,
      'Adult Male': 5063,
      'Kid Female': 133,
      'Kid Male': 590
    },
    'Camera-005': {
      'Old Female': 21,
      'Old Male': 1551,
      'Adult Female': 36,
      'Adult Male': 668,
      'Kid Female': 42,
      'Kid Male': 284
    }
  }
};

// Demographics Bar Chart Component
const DemographicsBarChart = ({ storeName, onImageClick }) => {
  const [currentImageSet, setCurrentImageSet] = useState(0);
  const date = useSelector((state) => state.customization.currentDate);

  // Get current demographic data based on selected date
  const getCurrentDemographicData = () => {
    if (!date) return null;

    const day = parseInt(date.split('-')[2]);

    if (day === 15 || day === 16) {
      return demographicData[day];
    }
    return null;
  };

  const currentData = getCurrentDemographicData();

  if (!currentData) {
    return (
      <div className="mt-4 p-4 bg-gray-50 rounded-lg">
        <div className="text-center text-gray-500 text-sm">No demographic data available for selected date</div>
      </div>
    );
  }

  // Map store names to camera IDs
  const storeToCamera = {
    'Fountain (Inside)': 'Camera-001',
    'Gate 1 (Inside)': 'Camera-002',
    'Gate 2 (Inside)': 'Camera-003',
    'Gate 3 (Inside)': 'Camera-004',
    'Gate 4 (Inside)': 'Camera-005'
  };

  const cameraId = storeToCamera[storeName];
  const cameraData = currentData[cameraId];

  if (!cameraData) {
    return (
      <div className="mt-4 p-4 bg-gray-50 rounded-lg">
        <div className="text-center text-gray-500 text-sm">No camera data available for {storeName}</div>
      </div>
    );
  }

  // Calculate totals for this specific camera
  const totalMale = (cameraData['Old Male'] || 0) + (cameraData['Adult Male'] || 0) + (cameraData['Kid Male'] || 0);
  const totalFemale = (cameraData['Old Female'] || 0) + (cameraData['Adult Female'] || 0) + (cameraData['Kid Female'] || 0);
  const grandTotal = totalMale + totalFemale;

  // Calculate percentages
  const malePercentage = grandTotal > 0 ? (totalMale / grandTotal) * 100 : 0;
  const femalePercentage = grandTotal > 0 ? (totalFemale / grandTotal) * 100 : 0;

  // Check if current date is 15th or 16th
  const shouldShowImages = () => {
    if (!date) return false;
    const day = parseInt(date.split('-')[2]);
    return day === 15 || day === 16;
  };

  // Function to get camera images for this store based on date
  // Function to get camera images for this store based on date
  const getCameraImages = () => {
    if (!shouldShowImages()) return [];

    if (typeof cameras === 'undefined') return [];

    // const day = parseInt(date.split('-')[2]);

    // Map store to camera ID
    const storeToCamera = {
      'Fountain (Inside)': 'Camera-001',
      'Gate 1 (Inside)': 'Camera-002',
      'Gate 2 (Inside)': 'Camera-003',
      'Gate 3 (Inside)': 'Camera-004',
      'Gate 4 (Inside)': 'Camera-005'
    };

    const cameraId = storeToCamera[storeName];
    const cameraImagesObj = cameras[cameraId] || {};

    // Extract all URLs from that camera
    const cameraImages = Object.values(cameraImagesObj);

    if (cameraImages.length === 0) return [];

    // Shuffle images randomly
    const shuffled = [...cameraImages].sort(() => Math.random() - 0.5);

    // Break into sets of 5
    const imageSets = [];
    for (let i = 0; i < shuffled.length; i += 5) {
      imageSets.push(shuffled.slice(i, i + 5));
    }

    return imageSets;
  };

  const imageSets = getCameraImages();
  const currentImages = imageSets[currentImageSet] || [];

  const handlePrevious = () => {
    setCurrentImageSet((prev) => (prev > 0 ? prev - 1 : imageSets.length - 1));
  };

  const handleNext = () => {
    setCurrentImageSet((prev) => (prev < imageSets.length - 1 ? prev + 1 : 0));
  };

  return (
    <div className="mt-4 p-4 bg-gray-50 rounded-lg">
      <div className="grid grid-cols-12 gap-4 items-center">
        {/* Bar Chart Section */}
        <div className="col-span-7">
          <div className="space-y-3">
            {/* Labels */}
            <div className="flex justify-between text-sm">
              <span style={{ color: '#10B981' }} className="font-medium">
                Male Devotees
              </span>
              <span style={{ color: '#1173F0' }} className="font-medium">
                Female Devotees
              </span>
            </div>

            {/* Bar Chart */}
            <div className="space-y-2">
              <div className="flex w-full h-6 bg-gray-200 rounded-full overflow-hidden">
                <div
                  className="h-full flex items-center justify-start pl-2"
                  style={{
                    width: `${malePercentage}%`,
                    backgroundColor: '#10B981'
                  }}
                >
                  {malePercentage > 15 && <span className="text-white text-xs font-medium">Male Devotees</span>}
                </div>
                <div
                  className="h-full flex items-center justify-start pl-2"
                  style={{
                    width: `${femalePercentage}%`,
                    backgroundColor: '#1173F0'
                  }}
                >
                  {femalePercentage > 15 && <span className="text-white text-xs font-medium">Female Devotees</span>}
                </div>
              </div>
            </div>

            {/* Values */}
            <div className="flex justify-between">
              <div className="text-left">
                <div className="text-2xl font-bold" style={{ color: '#10B981' }}>
                  {totalMale}
                </div>
                <div className="text-sm text-gray-600">Male Devotees Footfall</div>
              </div>
              <div className="text-right">
                <div className="text-2xl font-bold" style={{ color: '#1173F0' }}>
                  {totalFemale}
                </div>
                <div className="text-sm text-gray-600">Female Devotees Footfall</div>
              </div>
            </div>
          </div>
        </div>

        {/* Image Gallery Section */}
        <div className="col-span-5 relative mx-5">
          {shouldShowImages() && imageSets.length > 0 ? (
            <>
              {/* Navigation Arrows */}
              {imageSets.length > 1 && (
                <>
                  <button
                    onClick={handlePrevious}
                    className="absolute left-0 top-1/2 transform -mx-5 -translate-y-1/2 -translate-x-4 z-10 bg-white rounded-full p-2 shadow-md hover:bg-gray-50 transition-colors" // Changed -translate-x-2 to -translate-x-4 and p-1 to p-2
                  >
                    <ChevronLeft className="w-4 h-4 text-gray-600 " />
                  </button>
                  <button
                    onClick={handleNext}
                    className="absolute right-0 top-1/2 transform -mx-5 -translate-y-1/2 translate-x-2 z-10 bg-white rounded-full p-1 shadow-md hover:bg-gray-50 transition-colors"
                  >
                    <ChevronRight className="w-4 h-4 text-gray-600" />
                  </button>
                </>
              )}

              <div className="grid grid-cols-5 gap-1">
                {currentImages.map((image, index) => (
                  <div key={`${currentImageSet}-${index}`} className="relative group">
                    <img
                      src={image}
                      alt={`Evidence ${index + 1}`}
                      className="w-full h-40 object-cover rounded cursor-pointer hover:opacity-90 transition-opacity"
                      onClick={() => onImageClick(currentImages, index)}
                      onError={(e) => {
                        e.target.src =
                          'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMTAwIiBoZWlnaHQ9IjEwMCIgdmlld0JveD0iMCAwIDEwMCAxMDAiIGZpbGw9Im5vbmUiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+CjxyZWN0IHdpZHRoPSIxMDAiIGhlaWdodD0iMTAwIiBmaWxsPSIjRjNGNEY2Ii8+CjxwYXRoIGQ9Ik0zNSA0MEg2NVY2MEgzNVY0MFoiIGZpbGw9IiM5Q0EzQUYiLz4KPGnpcmNsZSBjeD0iNDUiIGN5PSI0NSIgcj0iMyIgZmlsbD0iIzlDQTNBRiIvPgo8L3N2Zz4K';
                      }}
                    />
                  </div>
                ))}
                {/* Fill empty slots if less than 5 images */}
                {Array.from({ length: Math.max(0, 5 - currentImages.length) }).map((_, index) => (
                  <div key={`empty-${index}`} className="w-full h-20 bg-gray-100 rounded flex items-center justify-center">
                    <span className="text-gray-400 text-xs">No image</span>
                  </div>
                ))}
              </div>
            </>
          ) : (
            <div className="text-center text-gray-400 text-sm py-8">No images available for selected date</div>
          )}
        </div>
      </div>
    </div>
  );
};

// ImageViewerModal Component
const ImageViewerModal = ({ open, onClose, images, initialIndex }) => {
  if (!open) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50" onClick={onClose}>
      <div className="max-w-4xl max-h-4xl p-4">
        <img
          src={images[initialIndex]}
          alt="Full View"
          className="max-w-full max-h-full object-contain"
          onClick={(e) => e.stopPropagation()}
        />
      </div>
    </div>
  );
};

// Individual Store Card Component
const StoreCard = ({ store, onImageClick, onAnalysisClick, showDemographics }) => {
  // const date = useSelector((state) => state.customization.currentDate);

  return (
    <div className="bg-white rounded-lg shadow-sm overflow-hidden">
      <div className="p-6">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <div className="w-3 h-3 bg-green-500 rounded-full"></div>
            <h2 className="text-lg font-medium text-gray-800">{store.name}</h2>
          </div>
          <div className="flex gap-2">
            <button
              className="px-3 py-1 bg-white border border-teal-500 text-teal-600 rounded text-sm hover:bg-teal-50 transition-colors"
              onClick={onAnalysisClick}
            >
              Analysis
            </button>
          </div>
        </div>

        {/* Demographics Bar Chart with integrated image gallery */}
        {showDemographics && <DemographicsBarChart storeName={store.name} storeId={store.id} onImageClick={onImageClick} />}

        <div className="grid grid-cols-12 gap-4 mt-6">
          {/* Left Section - Analytics */}
          <div className="col-span-12">
            <div className="text-sm text-gray-600 mt-1">{store.location}</div>
          </div>
        </div>
      </div>
    </div>
  );
};

const CameraPage = () => {
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedStoreImages, setSelectedStoreImages] = useState([]);
  const [selectedImageIndex, setSelectedImageIndex] = useState(0);

  const navigate = useNavigate();

  // Function to handle image click
  const handleImageClick = (images, imageIndex) => {
    if (!images || !Array.isArray(images) || images.length === 0) {
      console.error('No images available');
      return;
    }

    setSelectedStoreImages(images);
    setSelectedImageIndex(imageIndex);
    setModalOpen(true);
  };

  // ANALYSIS button -> go to StorePage route
  const handleAnalysisClick = () => {
    navigate('/main/stores/storeinsight'); // absolute path matching your routes
  };

  return (
    <div className="min-h-screen p-6">
      <div className="max-w-7xl mx-auto ">
        {/* Store Cards Grid */}
        <div className="space-y-6">
          {storeLocations.map((store) => (
            <StoreCard
              key={store.id}
              store={store}
              onImageClick={handleImageClick}
              onAnalysisClick={handleAnalysisClick}
              showDemographics={true} // Show demographics for all stores now
            />
          ))}
        </div>

        {/* Modal */}
        <ImageViewerModal
          open={modalOpen}
          onClose={() => setModalOpen(false)}
          images={selectedStoreImages}
          initialIndex={selectedImageIndex}
        />
      </div>
    </div>
  );
};

export default CameraPage;
